import React, { useCallback, useState } from "react";
import { toast } from "react-toastify";
import Dialog from "../../../components/ui/Dialog";
import EmployeePicker from "../../../components/ui/EmployeePicker";
import BranchSelect from "../../../components/ui/BranchSelect";
import { ASSET_CONDITIONS, ConditionText } from "./AssetBadges";
import { handleAssetActionError, employeeName } from "./assetErrors";
import {
  useAssignments,
  useAssignAsset,
  useReturnAsset,
  useTransferAsset,
  useMarkAssetLost,
  useSetAssetStatus,
  useMoveAssetBranch,
  useDeleteAsset,
  useSetDateReceived,
  useAssetDocuments,
  useUploadAssetDocument,
} from "../../../api/queries/assetQueries";
import { getApiErrorMessage } from "../../../utils/apiError";
import { addDaysToPlainDate, todayInManila } from "../../../utils/dateTime";

const MAX_NOTES = 500;
const MAX_DOC_MB = 10;
const DOC_ACCEPT =
  ".pdf,.jpg,.jpeg,.png,.webp,.heic,application/pdf,image/jpeg,image/png,image/webp,image/heic";
const DOC_CATEGORIES = [
  ["ACCOUNTABILITY_FORM", "Accountability form"],
  ["RECEIPT", "Receipt"],
  ["WARRANTY", "Warranty"],
  ["PHOTO", "Photo"],
  ["OTHER", "Other"],
];

/* ---------- small building blocks ---------- */
const AssetSummary = ({ asset }) => (
  <div className="asset-summary mb-3">
    <div className="fw-bold text-navy">
      {asset?.assetTag} · {asset?.name}
    </div>
    <div className="text-slate-500 fs-075">
      {[
        asset?.brand,
        asset?.model,
        asset?.serialNumber && `S/N ${asset.serialNumber}`,
      ]
        .filter(Boolean)
        .join(" · ") || "—"}
    </div>
  </div>
);

const Field = ({ label, htmlFor, required, children, hint }) => (
  <div className="mb-3">
    <label htmlFor={htmlFor} className="profile-label">
      {label} {required && <span className="text-danger">*</span>}
    </label>
    {children}
    {hint && <div className="text-slate-500 fs-070 mt-1">{hint}</div>}
  </div>
);

const NotesField = ({
  value,
  onChange,
  required,
  label = "Notes",
  placeholder,
}) => (
  <Field label={label} htmlFor="asset-notes" required={required}>
    <textarea
      id="asset-notes"
      className="form-control fs-085"
      rows={3}
      maxLength={MAX_NOTES}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
    />
    <div className="text-end text-slate-500 fs-070">
      {value.length}/{MAX_NOTES}
    </div>
  </Field>
);

const ConditionSelect = ({ id, value, onChange, required }) => (
  <select
    id={id}
    className="form-select"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    required={required}
  >
    {!required && <option value="">Same as now</option>}
    {required && !value && <option value="">Select condition</option>}
    {ASSET_CONDITIONS.map((c) => (
      <option key={c} value={c}>
        {c}
      </option>
    ))}
  </select>
);

const FormError = ({ message }) =>
  message ? (
    <div className="alert alert-danger fs-085 py-2" role="alert">
      <i className="bi bi-exclamation-triangle-fill me-2"></i>
      {message}
    </div>
  ) : null;

const Footer = ({
  onCancel,
  onSubmit,
  busy,
  disabled,
  label,
  tone = "primary",
}) => (
  <>
    <button
      type="button"
      className="btn btn-outline-secondary profile-btn"
      onClick={onCancel}
      disabled={busy}
    >
      Cancel
    </button>
    <button
      type="button"
      className={`btn btn-${tone} profile-btn`}
      onClick={onSubmit}
      disabled={busy || disabled}
    >
      {busy && (
        <span
          className="spinner-border spinner-border-sm"
          aria-hidden="true"
        ></span>
      )}
      {label}
    </button>
  </>
);

/** Optional "temporary loan" with a return date (tomorrow or later). */
const LoanFields = ({ isLoan, setIsLoan, date, setDate }) => (
  <div className="mb-3">
    <div className="form-check form-switch mb-2">
      <input
        id="asset-loan"
        type="checkbox"
        className="form-check-input"
        checked={isLoan}
        onChange={(e) => setIsLoan(e.target.checked)}
      />
      <label htmlFor="asset-loan" className="form-check-label fs-085">
        Temporary loan (set a return date)
      </label>
    </div>
    {isLoan && (
      <input
        type="date"
        className="form-control"
        aria-label="Expected return date"
        value={date}
        min={addDaysToPlainDate(todayInManila(), 1)}
        onChange={(e) => setDate(e.target.value)}
      />
    )}
  </div>
);

/** Return/Transfer/Lost need the ACTIVE assignment id; list rows don't include it. */
const useActiveAssignment = (asset, assignment) => {
  const lookup = useAssignments(
    { assetId: asset?.id, status: "ACTIVE", page: 1, limit: 1 },
    { enabled: !assignment && !!asset?.id },
  );
  return assignment || lookup.data?.data?.[0] || null;
};

/* ---------- dialogs ---------- */
const AssignDialog = ({ asset, onClose }) => {
  const [employee, setEmployee] = useState(null);
  const [isLoan, setIsLoan] = useState(false);
  const [returnDate, setReturnDate] = useState("");
  const [condition, setCondition] = useState(asset.condition || "");
  const [notes, setNotes] = useState("");
  const [dateReceived, setDateReceived] = useState(todayInManila());
  const [error, setError] = useState(null);
  const assign = useAssignAsset();
  // The server refuses to assign an asset without a document: ask for one here
  const documents = useAssetDocuments(asset.id);
  const uploadDocument = useUploadAssetDocument();
  const needsDocument =
    documents.isSuccess && (documents.data || []).length === 0;
  const [docFile, setDocFile] = useState(null);
  const [docCategory, setDocCategory] = useState("ACCOUNTABILITY_FORM");
  const busy = assign.isPending || uploadDocument.isPending;

  const pickDocument = (f) => {
    setError(null);
    if (f && f.size > MAX_DOC_MB * 1024 * 1024) {
      setError(`The document is larger than ${MAX_DOC_MB} MB.`);
      setDocFile(null);
      return;
    }
    setDocFile(f || null);
  };

  const submit = async () => {
    setError(null);
    if (needsDocument) {
      try {
        await uploadDocument.mutateAsync({
          assetId: asset.id,
          category: docCategory,
          file: docFile,
        });
      } catch (err) {
        const m = err?.response?.data?.message;
        setError(
          Array.isArray(m)
            ? m.join(" ")
            : getApiErrorMessage(err, "Could not upload the document."),
        );
        return;
      }
    }
    assign.mutate(
      {
        assetId: asset.id,
        employeeId: employee.id,
        expectedReturnDate: isLoan ? returnDate : undefined,
        condition: condition || undefined,
        notes: notes.trim() || undefined,
        dateReceived: dateReceived || undefined,
      },
      {
        onSuccess: () => {
          toast.success(
            `${asset.assetTag} assigned to ${employeeName(employee)}.`,
          );
          onClose();
        },
        onError: (err) =>
          setError(
            handleAssetActionError(err, {
              onConflict: onClose,
              conflictMessage: "Asset is no longer available.",
            }),
          ),
      },
    );
  };

  return (
    <Dialog
      title="Assign asset"
      onClose={onClose}
      busy={busy}
      footer={
        <Footer
          onCancel={onClose}
          onSubmit={submit}
          busy={busy}
          disabled={
            !employee ||
            (isLoan && !returnDate) ||
            documents.isLoading ||
            (needsDocument && !docFile)
          }
          label={needsDocument ? "Upload and assign" : "Assign"}
        />
      }
    >
      <AssetSummary asset={asset} />
      <FormError message={error} />
      {needsDocument && (
        <div className="alert alert-warning fs-085 py-2">
          <div className="fw-semibold mb-1">
            <i className="bi bi-file-earmark-lock me-1"></i>A document is
            required first
          </div>
          <div className="mb-2">
            This asset has no document yet. Attach one (e.g. the signed
            accountability form); it's uploaded, then the asset is assigned.
          </div>
          <div className="row g-2">
            <div className="col-sm-5">
              <select
                className="form-select form-select-sm"
                value={docCategory}
                onChange={(e) => setDocCategory(e.target.value)}
                aria-label="Document type"
                disabled={busy}
              >
                {DOC_CATEGORIES.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-sm-7">
              <input
                type="file"
                className="form-control form-control-sm"
                accept={DOC_ACCEPT}
                onChange={(e) => pickDocument(e.target.files?.[0])}
                disabled={busy}
                aria-label="Document file"
              />
            </div>
          </div>
          <div className="fs-070 mt-1">
            PDF, JPG, PNG, WEBP or HEIC, max {MAX_DOC_MB} MB.
          </div>
        </div>
      )}
      <Field label="Employee" required>
        <EmployeePicker
          companyId={asset.companyId}
          value={employee}
          onChange={setEmployee}
          autoFocus
        />
      </Field>
      <LoanFields
        isLoan={isLoan}
        setIsLoan={setIsLoan}
        date={returnDate}
        setDate={setReturnDate}
      />
      <Field label="Handover condition" htmlFor="assign-condition">
        <ConditionSelect
          id="assign-condition"
          value={condition}
          onChange={setCondition}
        />
      </Field>
      <Field
        label="Date received"
        htmlFor="assign-received"
        hint="The day the employee got the asset."
      >
        <input
          id="assign-received"
          type="date"
          className="form-control"
          value={dateReceived}
          max={todayInManila()}
          onChange={(e) => setDateReceived(e.target.value)}
        />
      </Field>
      <NotesField
        value={notes}
        onChange={setNotes}
        placeholder="e.g. With charger and laptop bag"
      />
    </Dialog>
  );
};

const ReturnDialog = ({ asset, assignment, onClose }) => {
  const active = useActiveAssignment(asset, assignment);
  const [condition, setCondition] = useState("");
  const [nextStatus, setNextStatus] = useState("");
  const [nextTouched, setNextTouched] = useState(false);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState(null);
  const returnAsset = useReturnAsset();

  // Default: Maintenance for Poor/Damaged, otherwise Available (until the user picks)
  const suggested =
    condition === "Poor" || condition === "Damaged"
      ? "Maintenance"
      : "Available";
  const effectiveNext = nextTouched ? nextStatus : condition ? suggested : "";

  const submit = () => {
    setError(null);
    returnAsset.mutate(
      {
        id: active.id,
        condition,
        nextStatus: effectiveNext || undefined,
        notes: notes.trim() || undefined,
      },
      {
        onSuccess: () => {
          toast.success(`${asset.assetTag} returned.`);
          onClose();
        },
        onError: (err) =>
          setError(handleAssetActionError(err, { onConflict: onClose })),
      },
    );
  };

  return (
    <Dialog
      title="Return asset"
      subtitle={
        active ? `From ${employeeName(active.employee)}` : "Loading assignment…"
      }
      onClose={onClose}
      busy={returnAsset.isPending}
      footer={
        <Footer
          onCancel={onClose}
          onSubmit={submit}
          busy={returnAsset.isPending}
          disabled={!active || !condition}
          label="Return"
        />
      }
    >
      <AssetSummary asset={asset} />
      <FormError message={error} />
      {active?.conditionOut && (
        <p className="fs-080 text-slate-600">
          Handed over in <ConditionText condition={active.conditionOut} />{" "}
          condition.
        </p>
      )}
      <Field label="Condition now" htmlFor="return-condition" required>
        <ConditionSelect
          id="return-condition"
          value={condition}
          onChange={setCondition}
          required
        />
      </Field>
      <Field
        label="Next status"
        htmlFor="return-next"
        hint="Maintenance is suggested for Poor or Damaged items."
      >
        <select
          id="return-next"
          className="form-select"
          value={effectiveNext}
          onChange={(e) => {
            setNextTouched(true);
            setNextStatus(e.target.value);
          }}
          disabled={!condition}
        >
          <option value="Available">Available</option>
          <option value="Maintenance">Maintenance</option>
        </select>
      </Field>
      <NotesField
        value={notes}
        onChange={setNotes}
        placeholder="e.g. Cracked screen"
      />
    </Dialog>
  );
};

const TransferDialog = ({ asset, assignment, onClose }) => {
  const active = useActiveAssignment(asset, assignment);
  const [employee, setEmployee] = useState(null);
  const [isLoan, setIsLoan] = useState(false);
  const [returnDate, setReturnDate] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState(null);
  const transfer = useTransferAsset();

  const submit = () => {
    setError(null);
    transfer.mutate(
      {
        id: active.id,
        employeeId: employee.id,
        expectedReturnDate: isLoan ? returnDate : null,
        notes: notes.trim() || undefined,
      },
      {
        onSuccess: () => {
          toast.success(
            `${asset.assetTag} transferred to ${employeeName(employee)}.`,
          );
          onClose();
        },
        onError: (err) =>
          setError(handleAssetActionError(err, { onConflict: onClose })),
      },
    );
  };

  return (
    <Dialog
      title="Transfer asset"
      subtitle={
        active
          ? `Currently with ${employeeName(active.employee)}`
          : "Loading assignment…"
      }
      onClose={onClose}
      busy={transfer.isPending}
      footer={
        <Footer
          onCancel={onClose}
          onSubmit={submit}
          busy={transfer.isPending}
          disabled={!active || !employee || (isLoan && !returnDate)}
          label="Transfer"
        />
      }
    >
      <AssetSummary asset={asset} />
      <FormError message={error} />
      <Field label="New employee" required>
        <EmployeePicker
          companyId={asset.companyId}
          value={employee}
          onChange={setEmployee}
          excludeId={active?.employeeId}
          autoFocus
        />
      </Field>
      <LoanFields
        isLoan={isLoan}
        setIsLoan={setIsLoan}
        date={returnDate}
        setDate={setReturnDate}
      />
      <NotesField
        value={notes}
        onChange={setNotes}
        placeholder="e.g. Replacement for Jane"
      />
    </Dialog>
  );
};

const LostDialog = ({ asset, assignment, onClose }) => {
  const active = useActiveAssignment(asset, assignment);
  const [notes, setNotes] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState(null);
  const markLost = useMarkAssetLost();

  const submit = () => {
    setError(null);
    markLost.mutate(
      { id: active.id, notes: notes.trim() },
      {
        onSuccess: () => {
          toast.success(`${asset.assetTag} marked as lost.`);
          onClose();
        },
        onError: (err) =>
          setError(handleAssetActionError(err, { onConflict: onClose })),
      },
    );
  };

  return (
    <Dialog
      title="Mark asset as lost"
      subtitle={
        active
          ? `Assigned to ${employeeName(active.employee)}`
          : "Loading assignment…"
      }
      onClose={onClose}
      busy={markLost.isPending}
      footer={
        <Footer
          onCancel={onClose}
          onSubmit={submit}
          busy={markLost.isPending}
          disabled={!active || !notes.trim() || !confirmed}
          label="Mark lost"
          tone="danger"
        />
      }
    >
      <AssetSummary asset={asset} />
      <FormError message={error} />
      <NotesField
        value={notes}
        onChange={setNotes}
        required
        label="What happened?"
        placeholder="e.g. Reported stolen 2026-10-01, police report filed"
      />
      <div className="form-check">
        <input
          id="lost-confirm"
          type="checkbox"
          className="form-check-input"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
        />
        <label htmlFor="lost-confirm" className="form-check-label fs-085">
          I confirm this asset is lost. The assignment closes and the asset
          status becomes Lost.
        </label>
      </div>
    </Dialog>
  );
};

const StatusDialog = ({ asset, status, onClose }) => {
  const [remarks, setRemarks] = useState("");
  const [error, setError] = useState(null);
  const setStatus = useSetAssetStatus();

  const submit = () => {
    setError(null);
    setStatus.mutate(
      { id: asset.id, status, remarks: remarks.trim() || undefined },
      {
        onSuccess: () => {
          toast.success(`${asset.assetTag} is now ${status}.`);
          onClose();
        },
        onError: (err) =>
          setError(handleAssetActionError(err, { onConflict: onClose })),
      },
    );
  };

  return (
    <Dialog
      title={`Set status: ${status}`}
      onClose={onClose}
      busy={setStatus.isPending}
      footer={
        <Footer
          onCancel={onClose}
          onSubmit={submit}
          busy={setStatus.isPending}
          label={`Set ${status}`}
          tone={status === "Lost" ? "danger" : "primary"}
        />
      }
    >
      <AssetSummary asset={asset} />
      <FormError message={error} />
      <NotesField
        value={remarks}
        onChange={setRemarks}
        label="Remarks"
        placeholder="e.g. Screen being replaced"
      />
    </Dialog>
  );
};

const MoveBranchDialog = ({ asset, onClose }) => {
  const [branchId, setBranchId] = useState(asset.branchId || null);
  const [error, setError] = useState(null);
  const move = useMoveAssetBranch();

  const submit = () => {
    setError(null);
    move.mutate(
      { id: asset.id, branchId },
      {
        onSuccess: () => {
          toast.success(`${asset.assetTag} moved.`);
          onClose();
        },
        onError: (err) =>
          setError(handleAssetActionError(err, { onConflict: onClose })),
      },
    );
  };

  return (
    <Dialog
      title="Move to another branch"
      subtitle={`Now at ${asset.branch?.branchName || "company level"}`}
      onClose={onClose}
      busy={move.isPending}
      footer={
        <Footer
          onCancel={onClose}
          onSubmit={submit}
          busy={move.isPending}
          disabled={(branchId || null) === (asset.branchId || null)}
          label="Move"
        />
      }
    >
      <AssetSummary asset={asset} />
      <FormError message={error} />
      <Field label="Branch" htmlFor="move-branch">
        <BranchSelect
          id="move-branch"
          companyId={asset.companyId}
          value={branchId}
          onChange={setBranchId}
          emptyLabel="Company level (no branch)"
        />
      </Field>
    </Dialog>
  );
};

const DeleteDialog = ({ asset, onClose, onDeleted }) => {
  const [error, setError] = useState(null);
  const remove = useDeleteAsset();

  const submit = () => {
    setError(null);
    remove.mutate(asset.id, {
      onSuccess: () => {
        toast.success(`${asset.assetTag} deleted.`);
        onClose();
        onDeleted?.();
      },
      onError: (err) =>
        setError(handleAssetActionError(err, { onConflict: onClose })),
    });
  };

  return (
    <Dialog
      title="Delete asset?"
      onClose={onClose}
      busy={remove.isPending}
      footer={
        <Footer
          onCancel={onClose}
          onSubmit={submit}
          busy={remove.isPending}
          label="Delete"
          tone="danger"
        />
      }
    >
      <AssetSummary asset={asset} />
      <FormError message={error} />
      <p className="fs-085 text-slate-600 mb-0">
        The asset is removed from the register. Its custody history is kept and
        it can still be found with “Show deleted”.
      </p>
    </Dialog>
  );
};

/**
 * One place to open any asset action from any screen:
 *   const { openAction, dialogs } = useAssetActions();
 *   openAction("assign", { asset });  openAction("status", { asset, status: "Retired" });
 *   ...render {dialogs} once.
 */
/** Change the date the employee received the asset (current assignment). */
export const DateReceivedDialog = ({ assignment, onClose }) => {
  const [date, setDate] = useState(
    assignment.dateReceived
      ? String(assignment.dateReceived).slice(0, 10)
      : todayInManila(),
  );
  const [error, setError] = useState(null);
  const save = useSetDateReceived();
  const submit = () =>
    save.mutate(
      { id: assignment.id, dateReceived: date },
      {
        onSuccess: () => {
          toast.success("Date received updated.");
          onClose();
        },
        onError: (err) =>
          setError(handleAssetActionError(err, { onConflict: onClose })),
      },
    );
  return (
    <Dialog
      title="Date received"
      onClose={onClose}
      busy={save.isPending}
      footer={
        <Footer
          onCancel={onClose}
          onSubmit={submit}
          busy={save.isPending}
          disabled={!date}
          label="Save"
        />
      }
    >
      <FormError message={error} />
      <Field label="Date received" htmlFor="received-date" required>
        <input
          id="received-date"
          type="date"
          className="form-control"
          value={date}
          max={todayInManila()}
          onChange={(e) => setDate(e.target.value)}
        />
      </Field>
    </Dialog>
  );
};

export const useAssetActions = ({ onDeleted } = {}) => {
  const [action, setAction] = useState(null); // { type, asset, assignment?, status? }
  const openAction = useCallback(
    (type, payload) => setAction({ type, ...payload }),
    [],
  );
  const close = useCallback(() => setAction(null), []);

  let dialogs = null;
  if (action) {
    const props = {
      asset: action.asset,
      assignment: action.assignment,
      onClose: close,
    };
    const key = `${action.type}-${action.asset?.id}`;
    if (action.type === "assign")
      dialogs = <AssignDialog key={key} {...props} />;
    if (action.type === "return")
      dialogs = <ReturnDialog key={key} {...props} />;
    if (action.type === "transfer")
      dialogs = <TransferDialog key={key} {...props} />;
    if (action.type === "lost") dialogs = <LostDialog key={key} {...props} />;
    if (action.type === "status")
      dialogs = <StatusDialog key={key} {...props} status={action.status} />;
    if (action.type === "branch")
      dialogs = <MoveBranchDialog key={key} {...props} />;
    if (action.type === "delete")
      dialogs = <DeleteDialog key={key} {...props} onDeleted={onDeleted} />;
  }
  return { openAction, dialogs };
};

/** Which actions make sense for an asset in its current status (per the spec). */
export const actionsForStatus = (status) => {
  switch (status) {
    case "Available":
      return [
        "assign",
        "edit",
        "branch",
        "status:Maintenance",
        "status:Retired",
        "status:Lost",
        "delete",
      ];
    case "Assigned":
      return ["return", "transfer", "lost", "edit"];
    case "Maintenance":
      return ["status:Available", "status:Retired", "edit", "branch", "delete"];
    case "Retired":
    case "Lost":
      return ["status:Available", "edit", "delete"];
    default:
      return ["edit"];
  }
};

export const ACTION_LABELS = {
  assign: { label: "Assign", icon: "bi-person-plus" },
  return: { label: "Return", icon: "bi-box-arrow-in-left" },
  transfer: { label: "Transfer", icon: "bi-arrow-left-right" },
  lost: { label: "Mark lost", icon: "bi-exclamation-octagon", danger: true },
  edit: { label: "Edit", icon: "bi-pencil-square" },
  branch: { label: "Move branch", icon: "bi-building" },
  "status:Available": { label: "Set Available", icon: "bi-check-circle" },
  "status:Maintenance": { label: "Set Maintenance", icon: "bi-tools" },
  "status:Retired": { label: "Retire", icon: "bi-archive" },
  "status:Lost": {
    label: "Set Lost",
    icon: "bi-question-octagon",
    danger: true,
  },
  delete: { label: "Delete", icon: "bi-trash", danger: true },
};
